// src/services/runningAccountService.js
/**
 * Running Account Service
 * Handles all API calls for running accounts (cuentas abiertas)
 * 
 * Backend API: /apitest/runningaccount
 */

import axios from 'axios';
import { generateExternalReference } from '../utils/invoiceUtils';
import { NODE_BACKEND_API_URL } from '../config';

const RUNNING_ACCOUNT_API_URL = `${NODE_BACKEND_API_URL}/runningaccount`;

/**
 * Get auth headers with JWT token
 * @returns {Object} Headers with authorization
 */
const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

/**
 * Get clientDatabaseName from localStorage authUser
 * @returns {string} Client database name or 'DUTTO_TEST' as fallback
 */
const getClientDatabaseName = () => {
    try {
        const authUserString = localStorage.getItem('authUser');
        if (authUserString) {
            const authUser = JSON.parse(authUserString);
            if (authUser?.clientDatabaseName) {
                return authUser.clientDatabaseName;
            }
        }
    } catch (error) {
        console.error('[RunningAccount] Error reading clientDatabaseName:', error);
    }
    return 'DUTTO_TEST'; // Fallback
};

/**
 * Transform cart items to backend format for running account
 * @param {Array} cartItems - Cart items from cart context
 * @returns {Array} Transformed items for backend
 */
const transformCartItems = (cartItems) => {
    return cartItems.map(item => ({
        ProductCode: item.ProductCode || null,
        ProductName: item.ProductDescription || item.ProductName || '',
        ProductId: item.ProductId || null,
        quantity: item.quantity || 1,
        splitter: item.splitter || 1,
        // Optional: For volume pricing
        isVolumePricingActive: item.isVolumePricingActive || false,
        // Only include RetailPrice for custom products or volume-priced items
        ...(item.isVolumePricingActive || item.ProductCode === 'CUSTOM' || !item.ProductId 
            ? { RetailPrice: item.RetailPrice || 0 } 
            : {})
    }));
};

export const runningAccountService = {
    /**
     * Get all running account clients
     * @returns {Promise<Array>} List of clients with running accounts
     */
    getClients: async () => {
        try {
            console.log('[RunningAccount] Fetching running account clients...');
            
            const response = await axios.get(
                `${RUNNING_ACCOUNT_API_URL}/clients`,
                { headers: getAuthHeaders() }
            );
            
            console.log('[RunningAccount] Clients fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error fetching clients:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Create a new running account client
     * Supports two modes:
     * - With CUIT: Full AFIP-validated client
     * - Sin CUIT: Minimal data client (only name required)
     * 
     * @param {Object} clientData - Client data
     * @param {string} clientData.documento_tipo - 'CUIT' or 'SIN_CUIT'
     * @param {string|null} clientData.documento_nro - CUIT number (null for SIN_CUIT)
     * @param {string} clientData.razon_social - Legal business name
     * @param {string} clientData.nombre - Display name
     * @param {string} clientData.condicion_impositiva - Tax condition
     * @param {string} clientData.direccion - Address (optional)
     * @param {string} clientData.localidad - City (optional)
     * @param {string} clientData.provincia - Province (optional)
     * @param {string} clientData.telefono - Phone (optional)
     * @param {string} clientData.email - Email (optional)
     * @param {string} clientData.notas - Notes (optional)
     * @returns {Promise} Created client data
     */
    createClient: async (clientData) => {
        try {
            console.log('[RunningAccount] Creating running account client:', clientData);
            
            const payload = {
                documento_tipo: clientData.documento_tipo || 'SIN_CUIT',
                documento_nro: clientData.documento_nro || null,
                razon_social: clientData.razon_social || clientData.nombre,
                nombre: clientData.nombre || clientData.razon_social,
                alias: clientData.alias || null,
                condicion_impositiva: clientData.condicion_impositiva || 'CONSUMIDOR FINAL',
                direccion: clientData.direccion || '',
                localidad: clientData.localidad || '',
                provincia: clientData.provincia || '',
                telefono: clientData.telefono || '',
                email: clientData.email || '',
                notas: clientData.notas || '',
                estado: clientData.estado || 'ACTIVA',
                clientDatabaseName: getClientDatabaseName()
            };
            
            const response = await axios.post(
                `${RUNNING_ACCOUNT_API_URL}/clients`,
                payload,
                { 
                    headers: getAuthHeaders(),
                    timeout: 30000
                }
            );
            
            console.log('[RunningAccount] Client created successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error creating client:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Edit an existing running account client
     * @param {number} clientId - The running account client ID
     * @param {Object} clientData - Updated client data (full-replace)
     * @returns {Promise} Updated client data
     */
    editClient: async (clientId, clientData) => {
        try {
            console.log('[RunningAccount] Editing running account client:', clientId, clientData);

            const payload = {
                document_type: clientData.document_type,
                document_number: clientData.document_number || null,
                business_name: clientData.business_name,
                display_name: clientData.display_name,
                alias: clientData.alias !== undefined ? clientData.alias : null,
                tax_condition: clientData.tax_condition || 'CONSUMIDOR FINAL',
                tax_condition_code: clientData.tax_condition_code || 'CF',
                address: clientData.address || null,
                locality: clientData.locality || null,
                province: clientData.province || null,
                phone: clientData.phone || null,
                email: clientData.email || null,
                notes: clientData.notes || null,
                credit_limit: clientData.credit_limit || null,
                ...(clientData.status ? { status: clientData.status } : {})
            };

            const response = await axios.patch(
                `${RUNNING_ACCOUNT_API_URL}/clients/${clientId}`,
                payload,
                {
                    headers: getAuthHeaders(),
                    timeout: 30000
                }
            );

            console.log('[RunningAccount] Client edited successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error editing client:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Add items to a client's running account;
        }
    },

    /**
     * Add items to a client's running account
     * @param {Object} data - Running account update data
     * @param {Array} data.cartItems - Cart items to add
     * @param {number} data.discountPercentage - Discount percentage
     * @param {Object} data.clientData - Selected client data
     * @param {Object} volumePricingFunctions - Volume pricing helper functions
     * @returns {Promise} Response with update result
     */
    addToRunningAccount: async (data, volumePricingFunctions = {}) => {
        try {
            const { getVolumePrice, getDisplayRetailPrice } = volumePricingFunctions;
            
            // Transform cart items with volume pricing if applicable
            const transformedItems = data.cartItems.map((item, index) => {
                const baseItem = {
                    ProductCode: item.ProductCode || null,
                    ProductName: item.ProductDescription || item.ProductName || '',
                    ProductId: item.ProductId || null,
                    quantity: item.quantity || 1,
                    splitter: item.splitter || 1
                };

                // Check if volume pricing is active for this item
                const isVolumeActive = item.isVolumePricingActive === true;
                
                if (isVolumeActive && typeof getVolumePrice === 'function' && typeof getDisplayRetailPrice === 'function') {
                    // Calculate volume-adjusted price
                    const basePrice = getDisplayRetailPrice(item.RetailPrice);
                    const volumePrice = getVolumePrice(basePrice, item.productId || item.ProductId, item.quantity);
                    
                    baseItem.RetailPrice = volumePrice;
                    baseItem.isVolumePricingActive = true;
                    
                    console.log(`[RunningAccount] Volume price for ${item.ProductCode}:`, {
                        original: item.RetailPrice,
                        volumePrice
                    });
                } else if (item.ProductCode === 'CUSTOM' || item.ProductCode === 'CustomProduct' || !item.ProductId) {
                    // Custom products always include RetailPrice
                    baseItem.RetailPrice = item.RetailPrice || 0;
                }

                // Always include RetailPrice for backend processing
                if (!baseItem.RetailPrice) {
                    baseItem.RetailPrice = item.RetailPrice || 0;
                }

                return baseItem;
            });

            // Build clientData - backend prefers 'id' (RA client ID)
            // Falls back to documento_nro if id not available
            const clientData = {};
            if (data.clientData?.id) {
                clientData.id = data.clientData.id;
            } else if (data.clientData?.documento_nro) {
                clientData.documento_nro = data.clientData.documento_nro;
            }
            // Optional: picker_name for tracking who picked up items
            if (data.clientData?.picker_name) {
                clientData.picker_name = data.clientData.picker_name;
            }

            const payload = {
                cartItems: transformedItems,
                discount: {
                    percentage: data.discountPercentage || 0
                },
                clientData,
                sessionId: data.sessionId || `cart_${Date.now()}`,
                externalReference: data.externalReference || generateExternalReference(),
                
                // Pickup and stock management
                pickedUp: data.pickedUp || false,
                pickedUpBy: data.pickedUpBy || null,
                affectsStock: data.affectsStock !== false  // Default: true
            };
            
            console.log('[RunningAccount] Adding to running account:', payload);
            
            const response = await axios.post(
                `${RUNNING_ACCOUNT_API_URL}/update`,
                payload,
                { 
                    headers: getAuthHeaders(),
                    timeout: 30000 // 30 seconds timeout
                }
            );
            
            console.log('[RunningAccount] Successfully added to running account:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error adding to running account:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Get running account details for a specific client
     * @param {string} clientId - Client document number or ID
     * @returns {Promise} Client's running account details
     */
    getClientAccount: async (clientId) => {
        try {
            console.log('[RunningAccount] Fetching account for client:', clientId);
            
            const response = await axios.get(
                `${RUNNING_ACCOUNT_API_URL}/client/${clientId}`,
                { headers: getAuthHeaders() }
            );
            
            console.log('[RunningAccount] Client account details:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error fetching client account:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Get client movements with optional filters
     * Used in "Cuentas Abiertas" tab to display account activity
     * 
     * @param {number|string} clientId - Client ID from /runningaccount/clients
     * @param {Object} filters - Optional filter parameters
     * @param {string} filters.startDate - Start date (YYYY-MM-DD)
     * @param {string} filters.endDate - End date (YYYY-MM-DD)
     * @param {string} filters.status - Filter by status: PENDIENTE, FACTURADO, ANULADO
     * @param {boolean} filters.includeInvoiced - Include already invoiced items (default: true)
     * @returns {Promise} Client movements with summary
     * 
     * @example Response:
     * {
     *   success: true,
     *   client: { id, documento_nro, razon_social, condicion_iva },
     *   summary: { totalDebe, totalHaber, saldoActual, productosSinFacturar },
     *   movements: [{ id, fecha, tipo, descripcion, items[], debe, haber, saldoAcumulado, invoiceNumber, status, ... }],
     *   pagination: { page, pageSize, totalItems, totalPages }
     * }
     */
    getClientMovements: async (clientId, filters = {}) => {
        try {
            console.log('[RunningAccount] Fetching movements for client:', clientId, 'Filters:', filters);
            
            // Build query parameters
            const params = {};
            
            // Include items array for CARGO movements (product details)
            if (filters.includeItems !== undefined) {
                params.includeItems = filters.includeItems;
            }
            if (filters.startDate) {
                params.startDate = filters.startDate;
            }
            if (filters.endDate) {
                params.endDate = filters.endDate;
            }
            if (filters.status) {
                params.status = filters.status;
            }
            if (filters.includeVoided !== undefined) {
                params.includeVoided = filters.includeVoided;
            }
            if (filters.includeArchived !== undefined) {
                params.includeArchived = filters.includeArchived;
            }
            if (filters.page) {
                params.page = filters.page;
            }
            if (filters.pageSize) {
                params.pageSize = filters.pageSize;
            }
            
            const response = await axios.get(
                `${RUNNING_ACCOUNT_API_URL}/client/${clientId}/movements`,
                { 
                    headers: getAuthHeaders(),
                    params 
                }
            );
            
            console.log('[RunningAccount] Client movements fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error fetching client movements:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Toggle isArchived for one or more movements.
     * The endpoint flips the current value (toggle semantics).
     * All-or-nothing: if any ID fails validation the whole batch is rejected.
     * @param {number[]} ids - Array of movement IDs to toggle
     * @returns {Promise} { success, updatedCount, data: [...] }
     */
    toggleMovementsArchived: async (ids) => {
        try {
            console.log('[RunningAccount] Toggling isArchived for movements:', ids);
            const response = await axios.patch(
                `${RUNNING_ACCOUNT_API_URL}/movements/archived`,
                { ids },
                { headers: getAuthHeaders() }
            );
            console.log('[RunningAccount] toggleMovementsArchived response:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error toggling archived:', error.response?.data || error.message);
            throw error.response?.data || error;
        }
    },

    /**
     * Toggle isPaid for one or more movements.
     * For CARGO movements: triggers a server-side credit check and creates a LIQUIDACION
     * entry per settled CARGO. Response includes { data, liquidaciones }.
     * For non-CARGO: free toggle, no credit check, liquidaciones will be [].
     * @param {number[]} ids - Array of movement IDs (all CARGOs must belong to same client)
     * @returns {Promise} { success, updatedCount, data: [...], liquidaciones: [...] }
     */
    toggleMovementsPaid: async (ids) => {
        try {
            console.log('[RunningAccount] Toggling isPaid for movements:', ids);
            const response = await axios.patch(
                `${RUNNING_ACCOUNT_API_URL}/movements/paid`,
                { ids },
                { headers: getAuthHeaders() }
            );
            console.log('[RunningAccount] toggleMovementsPaid response:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error toggling isPaid:', error.response?.data || error.message);
            throw error.response?.data || error;
        }
    },

    /**
     * Pre-calculate settlement eligibility before calling the endpoint.
     * Pure function — no side effects.
     * @param {object[]} selectedCargos  - CARGO movement objects with a .debe field
     * @param {number}   availableCredit - client's current available credit (>= 0)
     * @returns {{ batchTotal, availableCredit, shortfall, canSettle, creditAfter }}
     */
    calculateSettlement: (selectedCargos, availableCredit) => {
        const batchTotal = selectedCargos.reduce((sum, c) => sum + (c.debe || 0), 0);
        const shortfall  = Math.max(0, batchTotal - availableCredit);
        return {
            batchTotal,
            availableCredit,
            shortfall,
            canSettle: shortfall === 0,
            creditAfter: availableCredit - batchTotal,
        };
    },

    /**
     * Add a movement (payment, adjustment) to a client's running account
     * This updates the same ledger as addToRunningAccount (products)
     * 
     * @param {Object} movementData - Movement data
     * @param {number|string} movementData.clientId - Client ID
     * @param {string} movementData.clientDocumento - Client CUIT
     * @param {string} movementData.tipoMovimiento - PAGO, ADELANTO, AJUSTE_CREDITO, AJUSTE_DEBITO
     * @param {number} movementData.monto - Amount
     * @param {string} movementData.fecha - Date (YYYY-MM-DD)
     * @param {string} movementData.descripcion - Description
     * @param {string} movementData.paymentMethod - Payment method (for PAGO/ADELANTO)
     * @param {string} movementData.paymentReference - Receipt/reference number
     * @param {number} movementData.debe - Debit amount (for AJUSTE_DEBITO)
     * @param {number} movementData.haber - Credit amount (for PAGO, ADELANTO, AJUSTE_CREDITO)
     * @param {string} movementData.notas - Additional notes
     * @returns {Promise} Created movement data
     */
    addMovement: async (movementData) => {
        try {
            console.log('[RunningAccount] Adding movement:', movementData);
            
            // Resolve payment method from either field name variant
            const resolvedPaymentMethod = movementData.paymentMethod || movementData.metodoPago || null;

            const payload = {
                clientId: movementData.clientId,
                tipoMovimiento: movementData.tipoMovimiento,
                monto: movementData.monto,
                fecha: movementData.fecha || null, // Date in YYYY-MM-DD format (Argentina local date)
                // Controller reads 'paymentMethod' (English key). Also send 'metodoPago' as alias.
                paymentMethod: resolvedPaymentMethod,
                metodoPago: resolvedPaymentMethod,
                referenciaPago: movementData.paymentReference || movementData.referenciaPago || null,
                notas: movementData.notas || '',
                externalReference: movementData.externalReference || generateExternalReference()
            };
            
            const response = await axios.post(
                `${RUNNING_ACCOUNT_API_URL}/movement`,
                payload,
                { 
                    headers: getAuthHeaders(),
                    timeout: 30000
                }
            );
            
            console.log('[RunningAccount] Movement added successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error adding movement:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Void (anular) a movement
     * Creates a reversal movement and marks the original as voided
     * 
     * @param {number} movementId - Movement ID to void
     * @param {string} reason - Reason for voiding (required, minimum 5 characters)
     * @returns {Promise} Void result with original and reversal movements
     * 
     * @example Response:
     * {
     *   success: true,
     *   message: "Movimiento anulado exitosamente",
     *   data: {
     *     originalMovement: { id, transactionId, tipoMovimiento, status: "VOIDED", voidedAt, voidReason },
     *     reversalMovement: { id, transactionId, tipoMovimiento: "ANULACION", debe, haber, saldoAnterior, saldoNuevo },
     *     productosAnulados: 0
     *   }
     * }
     */
    voidMovement: async (movementId, reason) => {
        try {
            console.log('[RunningAccount] Voiding movement:', movementId, 'Reason:', reason);
            
            if (!reason || reason.length < 5) {
                throw new Error('El motivo de anulación debe tener al menos 5 caracteres');
            }
            
            // Backend uses Spanish field name
            const payload = {
                motivoAnulacion: reason
            };
            
            const response = await axios.post(
                `${RUNNING_ACCOUNT_API_URL}/movement/${movementId}/void`,
                payload,
                { 
                    headers: getAuthHeaders(),
                    timeout: 30000
                }
            );
            
            console.log('[RunningAccount] Movement voided successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error voiding movement:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Convert an existing AR client (from clients table) to a Running Account client
     * Creates a copy of the client data with a link back to the original via source_client_id
     * 
     * @param {number} sourceClientId - Client ID from the clients (AR) table
     * @param {Object} options - Optional parameters
     * @param {number} options.creditLimit - Optional credit limit (advisory only)
     * @param {string} options.notes - Additional notes
     * @returns {Promise} Created running account client data
     * 
     * @example Response:
     * {
     *   success: true,
     *   message: "Cliente convertido exitosamente a cuenta abierta",
     *   client: {
     *     id: 1,
     *     source_client_id: 4,
     *     document_type: "CUIT",
     *     document_number: "20315637679",
     *     business_name: "ROBERTO DANIEL ROLDAN",
     *     ...
     *   }
     * }
     */
    convertFromAR: async (sourceClientId, options = {}) => {
        try {
            console.log('[RunningAccount] Converting AR client to Running Account:', sourceClientId, options);
            
            const payload = {
                source_client_id: sourceClientId,
                credit_limit: options.creditLimit || null,
                notes: options.notes || null
            };
            
            const response = await axios.post(
                `${RUNNING_ACCOUNT_API_URL}/clients/convert`,
                payload,
                { 
                    headers: getAuthHeaders(),
                    timeout: 30000
                }
            );
            
            console.log('[RunningAccount] AR client converted successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error converting AR client:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Update pickup status for a CARGO movement
     * @param {number} movementId - Movement ID
     * @param {Object} data - Pickup update data
     * @param {boolean} data.pickedUp - New pickup status
     * @param {string} data.pickedUpBy - Person who picked up
     * @param {boolean} data.affectsStock - Should affect stock (default: true)
     * @returns {Promise<Object>} Updated movement
     */
    updatePickupStatus: async (movementId, data) => {
        try {
            console.log('[RunningAccount] Updating pickup status:', { movementId, data });
            
            const response = await axios.patch(
                `${RUNNING_ACCOUNT_API_URL}/movement/${movementId}/pickup`,
                {
                    pickedUp: data.pickedUp,
                    pickedUpBy: data.pickedUpBy || null,
                    affectsStock: data.affectsStock !== false
                },
                { 
                    headers: getAuthHeaders(),
                    timeout: 15000
                }
            );
            
            console.log('[RunningAccount] Pickup status updated:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error updating pickup:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Get aggregate statistics for all running account clients
     * Used when no specific client is selected in "Cuentas Abiertas" tab
     * 
     * @param {Object} filters - Optional filter parameters
     * @param {string} filters.startDate - Start date (YYYY-MM-DD)
     * @param {string} filters.endDate - End date (YYYY-MM-DD)
     * @param {string} filters.estado - Filter by client status: ACTIVA, SUSPENDIDA, INACTIVA
     * @returns {Promise} Aggregate statistics
     * 
     * @example Response (NEW STRUCTURED FORMAT):
     * {
     *   success: true,
     *   data: {
     *     // NEW: Structured format (v2)
     *     clients: { total, active, suspended, inactive, withBalance },
     *     balance: { total, average, highest },          // THE TRUTH - what customer owes
     *     movements: { total, today, thisWeek, thisMonth, byType },
     *     invoicing: {
     *       pendingItems: 8,                             // Count of items to invoice
     *       invoicedItems: 0,
     *       pendingItemsGross: 41044.61,                 // Items at LOAD price (before discount)
     *       pendingCargoNet: 36940.15,                   // Items AFTER discount (actual debit)
     *       pendingDiscountAmount: 4104.46,              // Discount applied
     *       financialAdjustmentsDebit: 10000.00,         // Adjustments without items
     *       financialCredits: 0,                         // Payments received
     *       invoicedTotal: 0
     *     },
     *     balanceBreakdown: {                            // Explains how balance is composed
     *       pendingCargoNet, financialAdjustmentsDebit, financialCredits,
     *       calculatedTotal, actualTotal
     *     },
     *     topClients: [...],
     *     
     *     // LEGACY: Backward compatibility (v1)
     *     totalClients, activeClients, totalPendingBalance, pendingItems, pendingTotal, ...
     *   }
     * }
     * 
     * @note Frontend uses `balance.total` as PRIMARY balance number (not pendingTotal)
     * @note `pendingTotal` (legacy) = pre-discount prices, confusing for users
     */
    getAggregateStatistics: async (filters = {}) => {
        try {
            console.log('[RunningAccount] Fetching aggregate statistics. Filters:', filters);
            
            // Build query parameters
            const params = {};
            
            if (filters.startDate) {
                params.startDate = filters.startDate;
            }
            if (filters.endDate) {
                params.endDate = filters.endDate;
            }
            if (filters.estado) {
                params.estado = filters.estado;
            }
            
            const response = await axios.get(
                `${RUNNING_ACCOUNT_API_URL}/statistics`,
                { 
                    headers: getAuthHeaders(),
                    params,
                    timeout: 15000
                }
            );
            
            console.log('[RunningAccount] Aggregate statistics fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error fetching aggregate statistics:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Mark running account items as invoiced
     * Links the generated invoice to the running account items
     * 
     * @param {Object} data - Invoice link data
     * @param {number} data.invoiceId - The generated invoice ID
     * @param {string} data.invoiceNumber - The invoice number (e.g., "00005-00000196")
     * @param {Array<number>} data.itemIds - Array of running account item IDs to mark as invoiced
     * @returns {Promise<Object>} Result of the operation
     * 
     * Expected response:
     * {
     *   success: true,
     *   message: "Items marked as invoiced",
     *   itemsUpdated: 2
     * }
     */
    markItemsAsInvoiced: async (data) => {
        try {
            console.log('[RunningAccount] Marking items as invoiced:', data);
            
            const response = await axios.post(
                `${RUNNING_ACCOUNT_API_URL}/items/mark-invoiced`,
                {
                    invoiceId: data.invoiceId,
                    invoiceNumber: data.invoiceNumber,
                    itemIds: data.itemIds
                },
                { 
                    headers: getAuthHeaders(),
                    timeout: 15000
                }
            );
            
            console.log('[RunningAccount] Items marked as invoiced:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error marking items as invoiced:', error.response?.data || error.message);
            // Don't throw - this is a non-critical operation
            // The invoice was already created successfully
            return {
                success: false,
                message: error.response?.data?.message || error.message
            };
        }
    },

    /**
     * Invoice running account items — Call 2 of the RA invoicing flow.
     * POST /runningaccount/invoice
     * Marks items as INVOICED, optionally creates a Caja entry (Caja gate active only when
     * paymentMethod is cash-equivalent AND isPaid is false).
     *
     * @param {Object} data
     * @param {number} data.clientId - RA client ID
     * @param {number} data.invoiceId - Invoice ID returned by Call 1
     * @param {string} data.invoiceNumber - Human-readable invoice number from Call 1
     * @param {string} data.paymentMethod - Readable payment method ("Efectivo", "Transferencia", …)
     * @param {boolean} data.isPaid - Must match the isPaid sent in Call 1
     * @param {Array}  data.selectedItems - Rich item array: { itemId, invoiceItemId, usePrecioActual, precioActual, precioCarga, cantidad }
     * @returns {Promise<Object>} { success, data: { updatedItems } }
     */
    invoiceRunningAccount: async (data) => {
        try {
            console.log('[RunningAccount] Invoicing items — Call 2:', data);

            const response = await axios.post(
                `${RUNNING_ACCOUNT_API_URL}/invoice`,
                {
                    clientId: data.clientId,
                    selectedItems: data.selectedItems,
                    invoiceId: data.invoiceId,
                    invoiceNumber: data.invoiceNumber,
                    paymentMethod: data.paymentMethod,
                    isPaid: data.isPaid
                },
                {
                    headers: getAuthHeaders(),
                    timeout: 15000
                }
            );

            console.log('[RunningAccount] Invoice items result:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error invoicing items (Call 2):', error.response?.data || error.message);
            return {
                success: false,
                message: error.response?.data?.message || error.message
            };
        }
    },

    /**
     * Void (cancel) a running account movement
     * Creates a reversal entry and marks original as VOIDED
     * 
     * @param {number} movementId - The ID of the movement to void
     * @param {string} motivoAnulacion - Reason for voiding (min 5 chars)
     * @returns {Promise<Object>} Result of the void operation
     * 
     * Expected response:
     * {
     *   success: true,
     *   message: "Movement voided successfully",
     *   voidedMovement: { id, status, voidedAt, voidReason },
     *   reversalMovement: { id, tipo, debe, haber, descripcion },
     *   newBalance: 0.00
     * }
     * 
     * Possible errors:
     * - MOVEMENT_NOT_FOUND: Movement doesn't exist
     * - ALREADY_VOIDED: Movement was already voided
     * - INVALID_OPERATION: Cannot void ANULACION movements
     * - HAS_ACTIVE_INVOICES: CARGO has items with active (non-cancelled) invoices
     * - INVALID_REASON: motivoAnulacion missing or too short
     */
    /**
     * Sync a CARGO movement's debit to current catalog prices (updatedDebe).
     * POST /movement/:movementId/sync-debe
     * Only valid for CARGO + PENDING movements. Cascades balance delta to all subsequent movements.
     * @param {number} movementId - Movement ID to sync
     * @returns {Promise<Object>} { success, changed, movementId, oldDebit, newDebit, delta, cascadeCount, ... }
     */
    syncMovementDebe: async (movementId) => {
        try {
            console.log('[RunningAccount] Syncing movement debe:', movementId);

            const response = await axios.post(
                `${RUNNING_ACCOUNT_API_URL}/movement/${movementId}/sync-debe`,
                {},
                {
                    headers: getAuthHeaders(),
                    timeout: 15000
                }
            );

            console.log('[RunningAccount] Movement debe synced:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error syncing movement debe:', error.response?.data || error.message);
            return {
                success: false,
                error: error.response?.data?.error || 'UNKNOWN_ERROR',
                message: error.response?.data?.message || error.message
            };
        }
    },

    voidMovement: async (movementId, motivoAnulacion) => {
        try {
            console.log('[RunningAccount] Voiding movement:', movementId, 'Reason:', motivoAnulacion);
            
            const response = await axios.post(
                `${RUNNING_ACCOUNT_API_URL}/movement/${movementId}/void`,
                { motivoAnulacion },
                { 
                    headers: getAuthHeaders(),
                    timeout: 15000
                }
            );
            
            console.log('[RunningAccount] Movement voided:', response.data);
            return response.data;
        } catch (error) {
            console.error('[RunningAccount] Error voiding movement:', error.response?.data || error.message);
            
            // Return structured error for UI handling
            return {
                success: false,
                error: error.response?.data?.error || 'UNKNOWN_ERROR',
                message: error.response?.data?.message || error.message
            };
        }
    }
};

export default runningAccountService;
